'use client';
import GovernanceAction from '@src/components/molecules/governance-action';
import GovernanceActionSearchCard from '@src/components/molecules/governance-action/governanceActionSearchCard';
import { useGetIndexedProposalsQuery } from '@src/store/governanceAction/api';
import type { sortType } from '@src/store/governanceAction/types';
import type { SortSlug } from '@src/lib/seo/proposalSort';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import Button from '@src/components/atoms/buttons/button';
import type { IIndexedGovernancAction } from '@src/models/types';
import { useRouter } from 'next/navigation';
import EmptySearchCard from '@src/components/cards/emptySearchCard';

/** URL slug → the value the proposals endpoint expects. */
const API_SORT: Record<SortSlug, sortType> = {
  newest: 'Newly Created' as sortType,
  expiry: 'Soon to Expire' as sortType,
};

/**
 * `initialQuery` comes from `?q=`, which is how the search box on a hub page
 * hands off: the search endpoint queries the whole corpus and cannot be scoped
 * to one hub, so hubs send you here with the term already applied.
 */
export default function GovernanceActionsClient({
  initialQuery = '',
  sort = 'newest',
}: {
  initialQuery?: string;
  sort?: SortSlug;
}) {
  // Search term and sort both come from the URL: their controls sit above this
  // component, in the server-rendered header and toolbar.
  const currentSorting = API_SORT[sort];
  // The term is owned by the URL now — the search box navigates here with it.
  const searchQuery = initialQuery;
  const [currentPage, setCurrentPage] = useState(1);
  const [mergedItems, setMergedItems] = useState<IIndexedGovernancAction[]>([]);
  // No `type` filter: the category chips above are the one way to narrow by
  // action type now, and they navigate to the hub page for that type.
  const { data, isLoading } = useGetIndexedProposalsQuery({
    page: currentPage,
    size: 10,
    sort: currentSorting,
    query: searchQuery,
  });

  const router = useRouter();

  const [isInSearchMode, setIsInSearchMode] = useState(false);
  const [hideLoadMoreBtn, setHideLoadMoreBtn] = useState(false);
  useEffect(() => {
    setCurrentPage(1);
    setMergedItems([]);
  }, [searchQuery, currentSorting]);

  useEffect(() => {
    if (data) {
      if (
        //highlight does not exist on regular search result
        isEmpty(searchQuery) &&
        data.items.length != 0 &&
        data.items[0].highlight === undefined
      ) {
        setIsInSearchMode(false);
        setMergedItems([]);
      } else {
        setIsInSearchMode(true);
        setMergedItems((prev) => [...prev, ...data.items]);
        setHideLoadMoreBtn(false);
      }
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center bg-white dark:bg-dark-neutral-950">
        <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-primary-900 dark:border-white"></div>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col items-center bg-white dark:bg-dark-neutral-950">
      {/*
        No `min-h-calc-142` here. The layout column is already `min-h-screen`,
        so this only ever added height: `100vh - 142px` plus `py-12` overflows
        the viewport on its own, and with the header above it the page always
        scrolled into a band of empty space.
      */}
      <div className="relative flex w-full max-w-[1440px] flex-col gap-6 px-4 pb-6 pt-6 md:px-[72px] md:pb-12 lg:px-[156px]">
        {data && data.items.length == 0 && (
          <EmptySearchCard
            message="No Governance Action found matching your Search. Please try another search."
            className="h-[600px]"
          />
        )}
        {data &&
          (isInSearchMode ? (
            mergedItems.map((item) => (
              <GovernanceActionSearchCard
                key={item.id}
                abstract={item.meta.abstract || ''}
                title={item.meta.title || ''}
                type={item.proposal.type}
                id={item.id}
                expireAt={item.expireAt}
                highlight={item.highlight}
                onCardClick={(id: string) => {
                  router.push(`/governance_actions/${id}`);
                }}
              />
            ))
          ) : (
            <GovernanceAction
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              proposalResponse={data}
            />
          ))}
        {data && isInSearchMode && data.items.length < data.totalCount && (
          <div className="flex w-full justify-center">
            {hideLoadMoreBtn ? (
              <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-primary-900 dark:border-white"></div>
            ) : (
              mergedItems.length > 0 && (
                <Button
                  className="w-fit"
                  onClick={() => {
                    setHideLoadMoreBtn(true);
                    setCurrentPage(currentPage + 1);
                  }}
                >
                  Load more
                </Button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
